/**
 * @fileoverview Live document previews for the Library Finder's large preview
 * surfaces (the columns view preview pane and the gallery center stage).
 *
 * The Finder's own thumbnail pipeline already covers images (direct URL) and
 * PDFs (first-page raster via pdf.js). This fills the gap for Office and text
 * formats by rendering them client-side with the renderers this project
 * already ships — mammoth (Word), SheetJS (Excel), and a small CSV/text
 * reader. Rendering is heavy, so this is only ever mounted for the single
 * selected file in a big preview surface (wired via the Finder's
 * `renderLargePreview` prop), never per grid/list tile.
 */

"use client";

import { useEffect, useMemo, useState } from "react";
import * as mammoth from "mammoth";
import * as XLSX from "xlsx";
import DOMPurify from "dompurify";
import { Loader2, AlertCircle } from "lucide-react";
import type { FileSystemFileItem } from "@/ui/composed/file-system/file-system";
import { libraryPreviewKind } from "./LibraryFilePreview.helpers";

/** How many rows of a spreadsheet/CSV to render in the compact preview. */
const PREVIEW_MAX_ROWS = 200;

/**
 * Renders a live preview for the selected file, or null when there's nothing
 * to render (unsupported kind, or the file has no resolvable URL). Callers
 * treat null as "fall back to the type-icon thumbnail".
 */
export function LibraryFilePreview({ file }: { file: FileSystemFileItem }) {
  const kind = libraryPreviewKind(file);
  const url = file.url ?? null;

  if (!kind || !url) return null;

  return (
    <div className="size-full overflow-auto bg-white text-left dark:bg-neutral-100">
      {kind === "docx" && <DocxBody url={url} />}
      {kind === "xlsx" && <XlsxBody url={url} />}
      {kind === "csv" && (
        <CsvBody
          url={url}
          delimiter={file.name?.endsWith(".tsv") ? "\t" : ","}
        />
      )}
      {kind === "text" && <TextBody url={url} />}
    </div>
  );
}

// --- Shared states ---------------------------------------------------------

function PreviewSpinner() {
  return (
    <div className="flex h-full items-center justify-center py-10">
      <Loader2 className="size-5 animate-spin text-neutral-400" />
    </div>
  );
}

function PreviewFailure({ message }: { message: string }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 px-4 py-10 text-center">
      <AlertCircle className="size-5 text-neutral-400" />
      <p className="text-xs text-neutral-500">{message}</p>
    </div>
  );
}

/** Fetch a file as an ArrayBuffer, tracking loading/error state. */
function useFileBuffer(url: string) {
  const [buffer, setBuffer] = useState<ArrayBuffer | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setBuffer(null);
    setError(false);
    setLoading(true);

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error("fetch failed");
        return res.arrayBuffer();
      })
      .then((arrayBuffer) => {
        if (cancelled) return;
        setBuffer(arrayBuffer);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [url]);

  return { buffer, error, loading };
}

// --- DOCX (mammoth) --------------------------------------------------------

function DocxBody({ url }: { url: string }) {
  const { buffer, error, loading } = useFileBuffer(url);
  const [html, setHtml] = useState<string | null>(null);
  const [renderError, setRenderError] = useState(false);

  useEffect(() => {
    if (!buffer) return;
    let cancelled = false;
    setHtml(null);
    setRenderError(false);

    mammoth
      .convertToHtml({ arrayBuffer: buffer })
      .then((result) => {
        if (cancelled) return;
        setHtml(DOMPurify.sanitize(result.value));
      })
      .catch(() => {
        if (!cancelled) setRenderError(true);
      });

    return () => {
      cancelled = true;
    };
  }, [buffer]);

  if (loading || (buffer && html === null && !renderError)) {
    return <PreviewSpinner />;
  }
  if (error || renderError || html === null) {
    return <PreviewFailure message="Could not render Word document." />;
  }

  return (
    <>
      <div
        className="library-docx-preview p-6"
        // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml -- html state is only ever set to DOMPurify.sanitize(mammoth output) in the convert effect above
        dangerouslySetInnerHTML={{ __html: html }}
      />
      <style>{`
        .library-docx-preview { color: #1a1a1a; font-family: system-ui, sans-serif; font-size: 0.8125rem; line-height: 1.6; }
        .library-docx-preview h1 { font-size: 1.35rem; font-weight: 700; margin: 1.25rem 0 0.6rem; }
        .library-docx-preview h2 { font-size: 1.15rem; font-weight: 600; margin: 1rem 0 0.5rem; }
        .library-docx-preview h3 { font-size: 1rem; font-weight: 600; margin: 0.85rem 0 0.4rem; }
        .library-docx-preview p { margin: 0.45rem 0; }
        .library-docx-preview ul, .library-docx-preview ol { padding-left: 1.4rem; margin: 0.45rem 0; }
        .library-docx-preview li { margin: 0.2rem 0; }
        .library-docx-preview table { width: 100%; border-collapse: collapse; margin: 0.85rem 0; font-size: 0.75rem; }
        .library-docx-preview th, .library-docx-preview td { border: 1px solid #e5e7eb; padding: 0.4rem 0.6rem; text-align: left; }
        .library-docx-preview th { background: #f9fafb; font-weight: 600; }
        .library-docx-preview img { max-width: 100%; height: auto; border-radius: 0.375rem; }
        .library-docx-preview strong, .library-docx-preview b { font-weight: 600; }
      `}</style>
    </>
  );
}

// --- XLSX (SheetJS) --------------------------------------------------------

function XlsxBody({ url }: { url: string }) {
  const { buffer, error, loading } = useFileBuffer(url);
  const [activeSheet, setActiveSheet] = useState(0);

  const workbook = useMemo(() => {
    if (!buffer) return null;
    try {
      return XLSX.read(buffer, { type: "array" });
    } catch {
      return null;
    }
  }, [buffer]);

  const rows = useMemo(() => {
    if (!workbook) return null;
    const sheetName = workbook.SheetNames[activeSheet];
    if (!sheetName) return null;
    const ws = workbook.Sheets[sheetName];
    if (!ws) return null;
    return XLSX.utils.sheet_to_json<string[]>(ws, { header: 1, defval: "" });
  }, [workbook, activeSheet]);

  if (loading) return <PreviewSpinner />;
  if (error || !workbook) {
    return <PreviewFailure message="Could not render spreadsheet." />;
  }

  return (
    <SheetTable
      rows={rows ?? []}
      sheetNames={workbook.SheetNames}
      activeSheet={activeSheet}
      onSelectSheet={setActiveSheet}
    />
  );
}

// --- CSV / TSV -------------------------------------------------------------

function CsvBody({ url, delimiter }: { url: string; delimiter: string }) {
  const [rows, setRows] = useState<string[][] | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError(false);
    setLoading(true);

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error("fetch failed");
        return res.text();
      })
      .then((text) => {
        if (cancelled) return;
        setRows(parseDelimited(text, delimiter));
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [url, delimiter]);

  if (loading) return <PreviewSpinner />;
  if (error || !rows || rows.length === 0) {
    return <PreviewFailure message="Could not load preview." />;
  }

  return <SheetTable rows={rows} />;
}

// --- Plain text ------------------------------------------------------------

function TextBody({ url }: { url: string }) {
  const [content, setContent] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setContent(null);
    setError(false);
    setLoading(true);

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error("fetch failed");
        return res.text();
      })
      .then((text) => {
        if (cancelled) return;
        // Cap the preview so a huge log file doesn't freeze the pane.
        setContent(text.slice(0, 50_000));
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [url]);

  if (loading) return <PreviewSpinner />;
  if (error || content === null) {
    return <PreviewFailure message="Could not load preview." />;
  }

  return (
    <pre className="whitespace-pre-wrap p-4 font-mono text-[0.7rem] leading-relaxed text-neutral-800">
      {content}
    </pre>
  );
}

// --- Shared table ----------------------------------------------------------

function SheetTable({
  rows,
  sheetNames,
  activeSheet,
  onSelectSheet,
}: {
  rows: string[][];
  sheetNames?: string[];
  activeSheet?: number;
  onSelectSheet?: (index: number) => void;
}) {
  const headers = rows[0] ?? [];
  const dataRows = rows.slice(1);
  const truncated = dataRows.length > PREVIEW_MAX_ROWS;
  const displayRows = truncated
    ? dataRows.slice(0, PREVIEW_MAX_ROWS)
    : dataRows;

  return (
    <div className="flex flex-col gap-2 p-3">
      {sheetNames && sheetNames.length > 1 && onSelectSheet ? (
        <div className="flex flex-wrap gap-1">
          {sheetNames.map((name, index) => (
            <button
              key={name}
              type="button"
              onClick={() => onSelectSheet(index)}
              className={`rounded-md border px-2 py-0.5 text-[0.7rem] transition-colors ${
                index === activeSheet
                  ? "border-neutral-800 bg-neutral-800 text-white"
                  : "border-neutral-200 bg-neutral-50 text-neutral-500 hover:bg-neutral-100"
              }`}
            >
              {name}
            </button>
          ))}
        </div>
      ) : null}

      <span className="text-[0.65rem] text-neutral-500">
        {dataRows.length} row{dataRows.length !== 1 ? "s" : ""} ·{" "}
        {headers.length} column{headers.length !== 1 ? "s" : ""}
        {truncated ? ` (showing first ${PREVIEW_MAX_ROWS})` : ""}
      </span>

      <div className="overflow-auto rounded-md border border-neutral-200">
        <table className="w-full text-[0.7rem] text-neutral-800">
          <thead className="sticky top-0 z-10">
            <tr className="bg-neutral-100">
              {headers.map((header, index) => (
                <th
                  key={index}
                  className="whitespace-nowrap border-b border-neutral-200 px-2.5 py-1.5 text-left font-medium uppercase tracking-wide text-neutral-500"
                >
                  {String(header) || `Col ${index + 1}`}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className="border-t border-neutral-100 hover:bg-neutral-50"
              >
                {row.map((cell, cellIndex) => (
                  <td
                    key={cellIndex}
                    className="max-w-[16rem] truncate whitespace-nowrap px-2.5 py-1"
                  >
                    {String(cell)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Minimal delimited parser that handles quoted fields and escaped quotes. */
function parseDelimited(text: string, delimiter: string): string[][] {
  const rows: string[][] = [];

  for (const line of text.split("\n")) {
    const trimmed = line.replace(/\r$/, "");
    if (!trimmed.trim()) continue;

    const cells: string[] = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < trimmed.length; i++) {
      const char = trimmed[i];
      if (inQuotes) {
        if (char === '"') {
          if (trimmed[i + 1] === '"') {
            current += '"';
            i++;
          } else {
            inQuotes = false;
          }
        } else {
          current += char;
        }
      } else if (char === '"') {
        inQuotes = true;
      } else if (char === delimiter) {
        cells.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
    cells.push(current.trim());
    rows.push(cells);
  }

  return rows;
}
